import { RefObject } from 'react';
import styled, { css } from 'styled-components';

import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';
import {
  lima,
  paradigmLightBlue,
  razzmatazz,
} from '@paradigm/design-system/src/colors';
import { getProductName } from '@paradigm/features/src/products/domain';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import {
  useMaybeTradeTapeItem,
  useTradeTapeItem,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';
import { TradeTapeItem } from '@paradigm/features/src/unified-rfqs/entities/shared';
import {
  primaryProductCode,
  strategyProductCodes,
} from '@paradigm/features/src/unified-rfqs/utils/legs';
import { getProductCodesDescription } from '@paradigm/features/src/unified-rfqs/utils/productCodes';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import { QuickActionsTradeTape } from '#/components/UnifiedRfqs/components/QuickActions';
import ProductLabel from '#/components/UnifiedRfqs/components/ProductLabel';
import VirtualizedRow from '#/components/rfq-common/TableRow';
import { StrategyDescription } from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import { useContextMenuActions } from '#/components/UnifiedRfqs/context-menu/context-menu';
import BottomTableRow, {
  DesktopRow,
} from '#/components/UnifiedRfqs/BottomTableRow';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { SpreadFromMarkColumnLabel } from '../components/bottom-tabs/SpreadFromMarkColumnLabel';
import { SpreadFromMarkCellLabel } from '../components/bottom-tabs/SpreadFromMarkCellLabel';

interface Props {
  readonly tradeId: TradeTapeItem['id'];
  readonly scrollableRef: RefObject<HTMLDivElement>;
}

export default function TradeRowContainer(props: Props) {
  const { scrollableRef, tradeId } = props;
  return (
    <VirtualizedRow
      id={tradeId}
      scrollableRef={scrollableRef}
      rowContent={() => <TradeRow tradeId={tradeId} />}
    />
  );
}

function TradeRow({ tradeId }: { tradeId: TradeTapeItem['id'] }) {
  const trade = useMaybeTradeTapeItem(tradeId);
  if (trade == null) {
    return null;
  }

  return <TradeRowContent tradeId={tradeId} />;
}

function TradeRowContent({ tradeId }: { tradeId: TradeTapeItem['id'] }) {
  const { open } = useContextMenuActions();
  const trade = useTradeTapeItem(tradeId);
  const isDrfq = trade.rfqType === 'drfq';
  const tradeDate = isDrfq ? trade.filled_at : trade.traded;
  const productCode = getTradeProductCode(trade);
  const productLabel = getTradeProductLabel(trade);
  const isDated = getTradeIsDated(trade);
  const auctionType = isDrfq ? 'RFQ' : 'OB';
  const dataTestId = `urfq-${trade.rfqType}-trade-tape-row-${trade.id}`;
  const strategyDescription = (
    <StrategyDescription>{trade.description}</StrategyDescription>
  );
  const quickActions = (
    <QuickActionsTradeTape productCode={productCode} trade={trade} />
  );
  const side = isDrfq ? trade.side : trade.action;
  const actionLabel = <ActionLabel aria-label="side">{side}</ActionLabel>;

  return (
    <Row
      onContextMenu={({ x, y }) => {
        open({
          detailsItemId: trade.id,
          rfqId: trade.rfq_id,
          anchorPoint: { x, y },
        });
      }}
      columns={[
        {
          id: 'date',
          name: 'Date',
          children: shortDateFormat(tradeDate),
        },
        {
          id: 'time',
          name: 'Time',
          children: hourFormat(tradeDate),
          mobileOptions: { isMainRow: true },
        },
        {
          id: 'auctionType',
          name: 'Auction',
          children: auctionType,
        },
        {
          id: 'product',
          name: 'Product',
          children: (
            <ProductLabel productCodes={getTradeStrategyProductCodes(trade)}>
              {productLabel}
            </ProductLabel>
          ),
        },
        {
          id: 'description',
          name: 'Description',
          mobileOptions: { isMainRow: true },
          children: strategyDescription,
        },
        {
          id: 'actions',
          name: 'Actions',
          mobileOptions: { hideLabel: true },
          children: quickActions,
        },
        {
          id: 'quantity',
          name: 'Qty',
          children: formatProductQty(
            trade.quantity,
            productCode,
            isDrfq,
            isDated,
          ),
        },
        {
          id: 'price',
          name: 'Price',
          mobileOptions: {
            secondaryLabelContent: <SpreadFromMarkColumnLabel />,
          },
          children: (
            <div>
              <span aria-label="price">{trade.displayValues.tradePrice}</span>
              <SpreadFromMarkCellLabel value={trade.displayValues.markOffset} />
            </div>
          ),
        },
        {
          id: 'refPrice',
          name: 'Ref Price',
          children: trade.displayValues.indexPrice,
        },
        {
          id: 'side',
          name: 'Side',
          children: actionLabel,
        },
        {
          id: 'quoteCurrency',
          name: 'Quote Currency',
          children: trade.quote_currency,
        },
      ]}
      dataTestId={dataTestId}
    />
  );
}

function getTradeProductCode(trade: TradeTapeItem) {
  if (trade.rfqType === 'grfq') {
    return trade.product_codes[0];
  }

  // A trade has no product_codes to fall back on, so fall back to the
  // first leg: without a code the row loses both its product name and
  // its quantity precision.
  return primaryProductCode(trade.legs) ?? trade.legs[0]?.product_code;
}

// trade.legs may include a hedge leg on a different product, which must
// not count towards "is this multi-product" — see strategyProductCodes.
// A GRFQ is never multi-product, and its product_codes carries the hedge
// too, so only the primary code belongs in the set.
function getTradeStrategyProductCodes(trade: TradeTapeItem) {
  return trade.rfqType === 'grfq'
    ? trade.product_codes.slice(0, 1)
    : strategyProductCodes(trade.legs);
}

export function getTradeProductLabel(trade: TradeTapeItem) {
  if (trade.rfqType === 'grfq') {
    return getProductName(getTradeProductCode(trade));
  }

  const legProductCodes = getTradeStrategyProductCodes(trade);
  return legProductCodes.length > 1
    ? getProductCodesDescription(legProductCodes)
    : getProductName(getTradeProductCode(trade));
}

function getTradeIsDated(trade: TradeTapeItem): boolean {
  if (trade.rfqType === 'grfq') {
    return false;
  }

  return isDatedRfqLegs(trade.legs, getTradeProductCode(trade));
}

export function TradeHeaderRow() {
  return (
    <HeaderRow>
      <span>Date</span>
      <span>Time</span>
      <span>Auction</span>
      <span>Product</span>
      <span>Description</span>
      <span>Actions</span>
      <span>Qty</span>
      <span>
        Price
        <SpreadFromMarkColumnLabel />
      </span>
      <span>Ref Price</span>
      <span>Side</span>
      <span>Quote Currency</span>
    </HeaderRow>
  );
}

const COLUMNS_CSS = css`
  grid-template-columns: 68px 50px 50px 190px 250px 200px 50px 80px 80px 80px 100px;
  grid-template-areas: 'date time auction prod desc actions qty price refPrice side currency';
`;

const Row = styled(BottomTableRow)`
  ${COLUMNS_CSS}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .mobile-bottom-table-row {
      display: grid;
      grid-template-columns: 47px auto;
    }
  }
`;

const HeaderRow = styled(DesktopRow)`
  ${COLUMNS_CSS}
  position: sticky;
  top: 0;
  z-index: 1;
  color: ${paradigmLightBlue};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ActionLabel = styled.span`
  font-size: 14px;
  ${(props) => props.children === 'SELL' && `color: ${razzmatazz};`}
  ${(props) => props.children === 'BUY' && `color: ${lima};`}
`;
